import { get, post, postForm } from "../../../../utils/httpServices";
import { captureError } from "../../../../utils/sentryUtils";
import { getStoredToken, decodeToken } from "../../../../utils/tokenUtils";
import {
  MOCK_INTERVIEW_ENDPOINTS,
  BLOB_STORAGE_ENDPOINTS,
  USER_ENDPOINTS,
  USER_ACTION_TYPES,
} from "../../../../services/apiService";

/**
 * Check if user can do a mock interview
 * @param {Function} setCheckingMockStatus - Setter for checking status
 * @param {Function} setCanDoMock - Setter for canDoMock state
 * @param {Function} toast - Toast function from Chakra UI
 */
export const checkMockInterviewStatus = async (
  setCheckingMockStatus,
  setCanDoMock,
  toast
) => {
  try {
    setCheckingMockStatus(true);
    const token = getStoredToken();
    if (!token) throw new Error("User not authenticated");
    const decoded = decodeToken(token);
    if (!decoded) throw new Error("Invalid token");
    const userId = parseInt(
      decoded[
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
      ]
    );
    const data = await get(
      USER_ENDPOINTS.CAN_USER_PERFORM_ACTION(
        userId,
        USER_ACTION_TYPES.MOCK_INTERVIEW
      ),
      {
        token,
      }
    );
    setCanDoMock(data.canDoMock);
    if (!data.canDoMock) {
      toast({
        title: "Cannot Start Interview",
        description:
          "You cannot do another interview right now. Please try again later.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  } catch (err) {
    captureError(err);
    console.error(err);
    toast({
      title: "Error",
      description: err.message,
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  } finally {
    setCheckingMockStatus(false);
  }
};

/**
 * Fetch questions for a major
 * @param {string} major - Major name
 * @param {Function} setLoading - Setter for loading state
 * @param {Function} setError - Setter for error state
 * @param {Function} setQuestions - Setter for questions state
 * @param {Function} setInterviewQuestions - Setter for interview questions state
 * @param {Function} setCommonQuestions - Setter for common questions state
 * @param {Function} setSpecialQuestions - Setter for special questions state
 * @param {Function} toast - Toast function from Chakra UI
 * @param {string} fallbackVoiceover - Fallback voiceover URL
 */
export const fetchQuestions = async (
  major,
  setLoading,
  setError,
  setQuestions,
  setInterviewQuestions,
  setCommonQuestions,
  setSpecialQuestions,
  toast,
  fallbackVoiceover
) => {
  try {
    setLoading(true);
    const data = await get(BLOB_STORAGE_ENDPOINTS.GET_RANDOM_QUESTIONS, {
      params: { majorName: major, count: 5 },
    });
    if (!Array.isArray(data) || data.length === 0) {
      setQuestions([
        {
          questionId: "fallback-1",
          title: "Tell me about yourself.",
          videoUrl: fallbackVoiceover,
        },
      ]);
    } else {
      setQuestions(data);

      // Filter questions into three categories
      const specialTitles = [
        "Opening Message",
        "Candidate Question",
        "Closing Message",
      ];
      const specialQuestionsObj = {};
      const commonQuestionsList = [];
      const interviewQuestionsList = [];

      data.forEach((question) => {
        if (specialTitles.includes(question.title)) {
          specialQuestionsObj[question.title] = question;
        } else if (question.title === "Common Question") {
          commonQuestionsList.push(question);
        } else {
          interviewQuestionsList.push(question);
        }
      });

      setSpecialQuestions(specialQuestionsObj);
      setCommonQuestions(commonQuestionsList);
      setInterviewQuestions(interviewQuestionsList);
    }
  } catch (err) {
    captureError(err);
    console.error(err);
    setError(err.message);
    const fallbackQuestion = {
      questionId: "fallback-1",
      title: "Tell me about yourself.",
      videoUrl: fallbackVoiceover,
    };
    setQuestions([fallbackQuestion]);
    setInterviewQuestions([fallbackQuestion]);
    setCommonQuestions([]);
    setSpecialQuestions({});
    toast({
      title: "Error loading questions",
      description: err.message,
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  } finally {
    setLoading(false);
  }
};

/**
 * Increase attempt count for user
 */
export const increaseAttemptCount = async () => {
  try {
    const token = getStoredToken();
    if (!token) throw new Error("User not authenticated");
    const decoded = decodeToken(token);
    if (!decoded) throw new Error("Invalid token");
    const userId = parseInt(
      decoded[
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
      ]
    );

    await post(MOCK_INTERVIEW_ENDPOINTS.INCREASE_ATTEMPT(userId), {});
  } catch (err) {
    captureError(err);
    // Don't block interview start if this fails
    console.error("Error incrementing attempt count:", err);
  }
};

/**
 * Submit mock interview videos
 * @param {Array} recordedAnswers - Array of recorded answers
 * @param {number} interviewDuration - Interview duration in seconds
 * @param {number} totalReplayCount - Total replay count
 * @param {Function} setSubmitting - Setter for submitting state
 * @param {Function} setShowThankYou - Setter for showThankYou state
 * @param {Function} setInterviewStartTime - Setter for interview start time
 * @param {Function} setInterviewDuration - Setter for interview duration
 * @param {Function} setIsInterviewActive - Setter for interview active state
 * @param {Function} toast - Toast function from Chakra UI
 */
export const handleSubmitVideos = async (
  recordedAnswers,
  interviewDuration,
  totalReplayCount,
  setSubmitting,
  setShowThankYou,
  setInterviewStartTime,
  setInterviewDuration,
  setIsInterviewActive,
  toast
) => {
  if (recordedAnswers.length === 0) {
    toast({
      title: "No recordings to submit",
      description: "Record at least one answer.",
      status: "warning",
      duration: 3000,
      isClosable: true,
    });
    return;
  }
  setSubmitting(true);
  try {
    const token = getStoredToken();
    if (!token) throw new Error("User not authenticated");
    const decoded = decodeToken(token);
    if (!decoded) throw new Error("Invalid token");
    const userId = parseInt(
      decoded[
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
      ]
    );

    const formData = new FormData();
    const validAnswers = recordedAnswers.filter(
      (a) => a.blob && a.blob.size > 0
    );
    if (validAnswers.length === 0)
      throw new Error("No valid video recordings found");

    console.log("[MockInterview upload] submit start", {
      recordedAnswersCount: recordedAnswers?.length,
      validAnswersCount: validAnswers.length,
      userId,
      interviewDuration,
      totalReplayCount,
      totalReplayCountType: typeof totalReplayCount,
    });

    validAnswers.forEach((answer, index) => {
      const fileName =
        answer.questionType === "common"
          ? `common_question_${answer.questionIdx + 1}.webm`
          : answer.questionType === "major"
          ? `major_question_${answer.questionIdx + 1}.webm`
          : `candidate_question.webm`;
      const file = new File([answer.blob], fileName, { type: "video/webm" });
      formData.append("Videos", file);

      // Log what we are about to send (avoid logging actual blob bytes)
      console.log("[MockInterview upload] video appended", {
        index,
        fileName,
        blobSize: answer.blob?.size,
        questionType: answer.questionType,
        questionIdx: answer.questionIdx,
        questionId: answer.questionId,
        questionIdType: typeof answer.questionId,
      });
    });

    const duration = interviewDuration;
    const durationString = `${Math.floor(duration / 60)}:${(duration % 60)
      .toString()
      .padStart(2, "0")}`;
    formData.append("Duration", durationString);
    formData.append("UserId", String(userId));
    formData.append("NbOfTry", String(totalReplayCount));

    // Add question IDs from both common and major questions
    let questionIdsAppended = 0;
    const appendedQuestionIds = [];
    validAnswers.forEach((answer) => {
      // Only skip when it's truly missing (do NOT skip `0`)
      if (answer.questionId !== undefined && answer.questionId !== null && answer.questionId !== "") {
        const qid = parseInt(String(answer.questionId), 10);
        if (Number.isNaN(qid)) {
          console.warn("[MockInterview upload] invalid questionId, skipping", {
            questionIdRaw: answer.questionId,
          });
          return;
        }
        formData.append("QuestionIds", String(qid));
        questionIdsAppended += 1;
        appendedQuestionIds.push(String(qid));
      }
    });

    const videosCount = formData.getAll ? formData.getAll("Videos").length : validAnswers.length;
    console.log("[MockInterview upload] FormData counts", {
      videosCount,
      questionIdsAppended,
      Duration: durationString,
      UserId: String(userId),
      NbOfTry: String(totalReplayCount),
      QuestionIdsAppended: appendedQuestionIds,
    });
    if (videosCount !== questionIdsAppended) {
      console.warn("[MockInterview upload] Count mismatch (videos != QuestionIds)", {
        videosCount,
        questionIdsAppended,
      });
      // Prevent hitting backend 400; surface a clearer client-side error
      throw new Error(
        `Count mismatch: videos=${videosCount}, QuestionIds=${questionIdsAppended}`
      );
    }

    let result;
    try {
      result = await postForm(
        BLOB_STORAGE_ENDPOINTS.UPLOAD_MOCK_INTERVIEW,
        formData,
        { token }
      );
      console.log("[MockInterview upload] success", result);
    } catch (err) {
      captureError(err);
      console.error("[MockInterview upload] request failed", {
        status: err?.status,
        message: err?.message,
        details: err?.details,
      });
      throw err;
    }
    toast({
      title: "Mock Interview completed successfully!",
      description: `Interview ID: ${result.mockInterviewId}. Duration: ${durationString}. Uploaded ${result.videoUrls.length} videos. Replays used: ${totalReplayCount}.`,
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    setShowThankYou(true);
    setInterviewStartTime(null);
    setInterviewDuration(0);
    setIsInterviewActive(false); // Interview completed, allow navigation
  } catch (err) {
    captureError(err);
    console.error(err);
    toast({
      title: "Upload failed",
      description: err.message,
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  } finally {
    setSubmitting(false);
  }
};
